import React from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Optional label so the console line tells you WHICH boundary caught it. */
  boundary?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * The app had no error boundary anywhere — a single render throw meant a
 * white screen for the whole product, with nothing to click and nothing in
 * the UI to explain it. This is the floor: a friendly card, a way back
 * home, and the actual error on the console for us.
 *
 * Mounted around <Routes> in App.tsx. Can also be wrapped around any
 * individual subtree (e.g. a gameplay phase) to contain the blast radius.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep the raw error — a swallowed stack is worse than no boundary.
    console.error(
      `[ErrorBoundary${this.props.boundary ? `:${this.props.boundary}` : ''}] render threw`,
      error,
      info?.componentStack,
    );
  }

  private handleHome = () => {
    // Full reload rather than a router navigate: React's tree is in an
    // unknown state after a throw, and a fresh boot is the only thing we
    // can actually promise the user works.
    window.location.assign('/home');
  };

  private handleRetry = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="app-shell wr-center" style={{ padding: 24 }}>
        <div
          className="card lg"
          role="alert"
          style={{ maxWidth: 380, width: '100%', textAlign: 'center', padding: 28 }}
        >
          <div style={{ fontSize: 40, lineHeight: 1, marginBottom: 12 }} aria-hidden>
            🙃
          </div>
          <h1 className="h3" style={{ marginBottom: 8 }}>Something went off-script</h1>
          <p style={{ fontSize: 15, color: 'var(--ink-3)', lineHeight: 1.45, margin: 0 }}>
            That's on us, not you. Nothing you played was lost — head back home
            and pick up where you left off.
          </p>

          <div className="wr-col gap-2" style={{ marginTop: 20 }}>
            <button type="button" className="btn primary full lg" onClick={this.handleHome}>
              Back to home
            </button>
            <button type="button" className="btn ghost full" onClick={this.handleRetry}>
              Try again
            </button>
          </div>

          {import.meta.env.DEV && (
            <pre
              style={{
                marginTop: 18,
                marginBottom: 0,
                textAlign: 'left',
                fontFamily: 'var(--mono)',
                fontSize: 13,
                color: 'var(--ink-3)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 160,
                overflowY: 'auto',
              }}
            >
              {error.message}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
